<template>
  <div class="isolate overflow-hidden padding-header">
    <div class="flow-root bg-gray-900 pb-16 pt-24 sm:pt-32 lg:pb-0">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="relative z-10 lg:pb-10">
          <h2
            class="mx-auto max-w-4xl text-center text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight text-white"
          >
            Planes y precios:
          </h2>
          <h2
            class="mx-auto mt-2 sm:mt-4 max-w-4xl text-center text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight text-white"
          >
            Una visión general y completa
          </h2>
          <p class="mx-auto mt-7 max-w-2xl text-center text-lg leading-8 text-white/80">
            Explora nuestros paquetes y encuentra el plan que mejor se ajuste a las necesidades de
            tu empresa.
          </p>
          <div class="mt-16 flex justify-center">
            <div
              class="grid grid-cols-2 gap-x-1 rounded-full bg-white/5 p-1 text-center text-xs font-semibold leading-5 text-white"
            >
              <div class="sr-only">Payment frequency</div>
              <div
                v-for="option in frequencies"
                :key="option.value"
                @click="selectFrequency(option)"
                :class="[
                  frequency.value === option.value ? 'bg-indigo-500' : '',
                  'cursor-pointer rounded-full px-2.5 py-1'
                ]"
              >
                <span>{{ option.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-3"
        >
          <svg
            viewBox="0 0 1208 1024"
            aria-hidden="true"
            class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0"
          >
            <ellipse
              cx="604"
              cy="512"
              fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)"
              rx="604"
              ry="512"
            />
            <defs>
              <radialGradient id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867">
                <stop stop-color="#7775D6" />
                <stop offset="1" stop-color="#E935C1" />
              </radialGradient>
            </defs>
          </svg>
          <div
            class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10"
            aria-hidden="true"
          />
          <div
            v-for="tier in tiers"
            :key="tier.id"
            :class="[
              tier.featured
                ? 'z-10 bg-white shadow-xl ring-1 ring-gray-900/10 lg:-mt-8'
                : 'bg-gray-800/80 ring-1 ring-white/10 lg:bg-transparent lg:pb-14 lg:ring-0',
              'relative rounded-2xl'
            ]"
          >
            <div
              v-if="tier.featured"
              class="bg-gradient-to-r from-indigo-900 via-indigo-600 to-indigo-900 text-center text-white rounded-t-xl -mt-4 py-2 font-extrabold"
            >
              Más popular
            </div>
            <div class="p-8 lg:pt-12 xl:p-6 xl:pt-8">
              <h3
                :id="tier.id"
                :class="[
                  tier.featured ? 'text-gray-900' : 'text-white',
                  'text-sm font-semibold leading-6'
                ]"
              >
                {{ tier.name }}
              </h3>
              <div
                class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch"
              >
                <div class="mt-2 flex items-center gap-x-4">
                  <p
                    :class="[
                      tier.featured ? 'text-gray-900 text-5xl' : 'text-white text-4xl',
                      'font-bold tracking-tight'
                    ]"
                  >
                    <span :class="[tier.featured ? 'text-3xl' : 'text-2xl']">$</span
                    >{{ tier.price[frequency.value] }}
                  </p>
                  <div class="leading-5">
                    <p :class="tier.featured ? 'text-sm text-gray-900' : 'text-sm text-white'">
                      USD
                    </p>
                    <p :class="tier.featured ? 'text-xs text-gray-500' : 'text-xs text-gray-400'">
                      {{ frequency.value }}
                    </p>
                  </div>

                  <div
                    v-if="frequency.value === 'ANUAL'"
                    class="leading-5 font-bold ml-2"
                    :class="tier.featured ? 'text-green-500' : 'text-green-400'"
                  >
                    <p>
                      <span class="text-2xl"> -{{ tier.discount[frequency.value] }} </span>%
                    </p>
                  </div>
                </div>
                <a
                  :href="tier.href"
                  :aria-describedby="tier.id"
                  :class="[
                    tier.featured
                      ? 'bg-indigo-600 shadow-sm hover:bg-indigo-500 focus-visible:outline-indigo-600'
                      : 'bg-white/10 hover:bg-white/20 focus-visible:outline-white',
                    'rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
                  ]"
                >
                  Prueba Orqui gratis
                </a>
              </div>
              <div class="mt-8 flow-root sm:mt-10">
                <ul
                  role="list"
                  :class="[
                    tier.featured
                      ? 'divide-gray-900/5 border-gray-900/5 text-gray-800'
                      : 'divide-white/5 border-white/5 text-white',
                    '-my-2 divide-y border-t text-sm leading-6 lg:border-t-0'
                  ]"
                >
                  <div v-if="tier?.primaryList">
                    <ul>
                      <li
                        v-for="(primaryItem, index) in tier.primaryList"
                        :key="index"
                        class="flex gap-x-3 py-2"
                      >
                        <template v-if="index === 0">
                          <p class="text-xs">{{ primaryItem }}</p>
                        </template>
                        <template v-else>
                          <div aria-hidden="true"></div>
                          {{ primaryItem }}
                        </template>
                      </li>
                    </ul>
                  </div>

                  <div v-if="tier && tier.secondaryList">
                    <ul>
                      <li
                        v-for="(secondaryItem, index) in tier.secondaryList"
                        :key="index"
                        class="flex gap-x-3 py-2"
                      >
                        <template v-if="index === 0">
                          <p class="text-xs mt-4">{{ secondaryItem }}</p>
                        </template>
                        <template v-else>
                          <div aria-hidden="true"></div>
                          {{ secondaryItem }}
                        </template>
                      </li>
                    </ul>
                  </div>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relative bg-gray-50 lg:pt-8">
      <div class="mx-auto max-w-7xl pt-6 lg:pt-16 pb-10 text-gray-500 pl-8 text-xs">
        <p>*Los precios indicados son el costo base, no incluyen impuestos.</p>
      </div>

      <div class="mx-auto max-w-7xl px-6 lg:py-16 lg:px-8">
        <!-- Feature comparison (up to lg) -->
        <section aria-labelledby="mobile-comparison-heading" class="lg:hidden">
          <div class="mx-auto max-w-2xl space-y-16 mt-12">
            <div v-for="tier in tiers" :key="tier.id" class="border-t border-gray-900/10">
              <div>
                <h2 class="pt-8 font-semibold text-2xl">{{ tier.name }}</h2>
              </div>
              <div class="mt-4 space-y-10">
                <div v-for="section in sections" :key="section.name">
                  <h4 class="text-sm font-semibold leading-6 text-gray-900">{{ section.name }}</h4>
                  <div class="relative mt-6">
                    <!-- Fake card background -->
                    <div
                      aria-hidden="true"
                      class="absolute inset-y-0 right-0 hidden w-1/2 rounded-lg bg-white shadow-sm sm:block"
                    />

                    <div
                      class="relative rounded-lg bg-white shadow-sm sm:rounded-none sm:bg-transparent sm:shadow-none sm:ring-0"
                    >
                      <dl class="divide-y divide-gray-200 text-sm leading-6">
                        <div
                          v-for="feature in section.features"
                          :key="feature.name"
                          class="flex items-center justify-between px-4 py-3 sm:grid sm:grid-cols-2 sm:px-0"
                        >
                          <div class="flex items-center">
                            <dt class="text-gray-600">{{ feature.name }}</dt>
                            <div class="group relative">
                              <Image
                                v-if="feature.tooltip !== ''"
                                src="/images/icons/info.png"
                                alt="Alert Icon"
                                class="!w-4 !h-4 mx-2 cursor-pointer opacity-50"
                              />
                              <span
                                v-if="feature.tooltip"
                                class="hidden group-hover:flex absolute z-10 p-2 min-w-[16rem] bg-black text-white text-xs rounded-lg shadow-md left-1/2 transform -translate-x-1/2 -translate-y-full mt-[-1.5rem]"
                                >{{ feature.tooltip }}</span
                              >
                            </div>
                          </div>
                          <dd class="flex items-center justify-end sm:justify-center sm:px-4">
                            <span
                              v-if="typeof feature.tiers[tier.name] === 'string'"
                              :class="[
                                tier.featured ? 'font-semibold' : 'text-gray-900',
                                'text-right'
                              ]"
                            >
                              {{ feature.tiers[tier.name] }}
                            </span>
                            <template v-else>
                              <Image
                                v-if="feature.tiers[tier.name] === true"
                                alt="check-mark"
                                class="!mx-auto !h-4 !w-4 !opacity-75"
                                src="/images/icons/correct.png"
                              />
                              <Image
                                v-else
                                alt="X"
                                class="!mx-auto !h-3 !w-3 !opacity-80"
                                src="/images/icons/x-button.png"
                              />
                              <span class="sr-only">
                                {{ feature.tiers[tier.name] === true ? 'Yes' : 'No' }}
                              </span>
                            </template>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <!-- Fake card border -->
                    <div
                      aria-hidden="true"
                      :class="[
                        'pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 rounded-lg sm:block'
                      ]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Feature comparison (lg+) -->
        <section aria-labelledby="comparison-heading" class="hidden lg:block">
          <h2 id="comparison-heading" class="sr-only">Feature comparison</h2>

          <div class="grid grid-cols-4 gap-x-8 before:block py-3">
            <h3 class="text-black text-lg font-semibold leading-6 text-center">Básico</h3>
            <h3 class="text-black text-lg font-semibold leading-6 text-center">Profesional</h3>
            <h3 class="text-black text-lg font-semibold leading-6 text-center">Premium</h3>
          </div>

          <div class="space-y-16 border-t border-gray-900/10 pt-4">
            <div v-for="section in sections" :key="section.name">
              <h3 class="text-sm font-semibold leading-6 text-gray-900">{{ section.name }}</h3>
              <div class="relative -mx-8 mt-10">
                <!-- Fake card backgrounds -->
                <div
                  class="absolute inset-x-8 inset-y-0 grid grid-cols-4 gap-x-8 before:block"
                  aria-hidden="true"
                >
                  <div class="h-full w-full rounded-lg" />
                  <div class="h-full w-full rounded-lg" />
                  <div class="h-full w-full rounded-lg" />
                </div>

                <table class="relative w-full border-separate border-spacing-x-8">
                  <thead>
                    <tr class="text-left">
                      <th scope="col">
                        <span class="sr-only">Feature</span>
                      </th>
                      <th v-for="tier in tiers" :key="tier.id" scope="col">
                        <span class="sr-only">{{ tier.name }} tier</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(feature, featureIdx) in section.features" :key="feature.name">
                      <th
                        scope="row"
                        class="w-1/4 py-3 pr-4 text-left text-sm font-normal leading-6 text-gray-900"
                      >
                        <div class="relative flex items-center">
                          <span class="mr-2">{{ feature.name }}</span>
                          <div class="group relative">
                            <Image
                              v-if="feature.tooltip !== ''"
                              src="/images/icons/info.png"
                              alt="Alert Icon"
                              class="!w-4 !h-4 cursor-pointer opacity-50"
                            />
                            <span
                              v-if="feature.tooltip"
                              class="hidden group-hover:flex absolute z-10 p-2 min-w-[16rem] bg-black text-white text-xs rounded-lg shadow-md bottom-full mb-2 mt-[-1.50rem]"
                              >{{ feature.tooltip }}</span
                            >
                          </div>
                        </div>
                        <div
                          v-if="featureIdx !== section.features.length - 1"
                          class="absolute inset-x-8 mt-3 h-px bg-gray-200"
                        />
                      </th>
                      <td
                        v-for="tier in tiers"
                        :key="tier.id"
                        class="relative w-1/4 px-4 py-0 text-center"
                      >
                        <span class="relative h-full w-full py-3">
                          <span
                            v-if="typeof feature.tiers[tier.name] === 'string'"
                            class="text-gray-900 text-sm leading-6"
                            >{{ feature.tiers[tier.name] }}</span
                          >
                          <template v-else>
                            <Image
                              v-if="feature.tiers[tier.name] === true"
                              alt="Correct"
                              class="!mx-auto !h-3 !w-3 !opacity-75"
                              src="/images/icons/correct.png"
                              aria-hidden="true"
                            />
                            <Image
                              v-else
                              alt="X"
                              class="!mx-auto !h-3 !w-3"
                              src="/images/icons/x-button.png"
                              aria-hidden="true"
                            />
                            <span class="sr-only">{{
                              feature.tiers[tier.name] === true ? 'Yes' : 'No'
                            }}</span>
                          </template>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Fake card borders -->
                <div
                  class="pointer-events-none absolute inset-x-8 inset-y-0 grid grid-cols-4 gap-x-8 before:block"
                  aria-hidden="true"
                >
                  <div v-for="tier in tiers" :key="tier.id" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="isolate overflow-hidden mt-24">
          <div class="flow-root bg-gray-900 pb-16 pt-10 sm:pt-16 lg:pb-0 rounded-3xl">
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
              <div class="relative z-10 mb-24">
                <h2
                  class="mx-auto max-w-5xl text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-medium tracking-tight text-white"
                >
                  Complementos adicionales
                </h2>
                <p class="mx-auto mt-4 max-w-5xl text-lg leading-8 text-white/80">
                  ¿Necesitas que más empleados tengan acceso? ¿Requieres números de teléfono
                  adicionales?
                </p>
                <p class="mx-auto max-w-5xl text-lg leading-8 text-white/80">
                  Adapta Orqui AI a tus necesidades específicas con nuestras opciones de
                  personalización.
                </p>
              </div>

              <div
                class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-3"
              >
                <svg
                  viewBox="0 0 1208 1024"
                  aria-hidden="true"
                  class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0"
                >
                  <ellipse
                    cx="604"
                    cy="512"
                    fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)"
                    rx="604"
                    ry="512"
                  />
                  <defs>
                    <radialGradient id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867">
                      <stop stop-color="#7775D6" />
                      <stop offset="1" stop-color="#E935C1" />
                    </radialGradient>
                  </defs>
                </svg>
                <div
                  class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10"
                  aria-hidden="true"
                />

                <!--         cards 1111    -->
                <div
                  class="bg-gray-800/80 ring-1 ring-white/10 lg:bg-transparent lg:pb-14 lg:ring-0 relative rounded-2xl"
                >
                  <div class="p-8 lg:pt-12 xl:p-6 xl:pt-8">
                    <h3 class="text-white text-lg font-semibold leading-6">
                      Acceso adicional para asesores
                    </h3>
                    <p class="text-sm mt-2 text-gray-300">
                      Agrega asesores de una
                      <br />
                      forma rápida y sencilla.
                    </p>
                    <div
                      class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch"
                    >
                      <div class="mt-6 flex items-center gap-x-4">
                        <p class="text-white text-4xl font-bold tracking-tight">$10</p>
                        <div class="text-xs font-bold text-white">
                          <p>USD</p>
                          <p>Mensual</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="z-10 bg-white shadow-xl ring-1 ring-gray-900/10 relative rounded-2xl -mt-4"
                >
                  <div class="p-8 lg:pt-12 xl:p-6 xl:pt-8">
                    <h3 class="text-gray-900 text-lg font-semibold leading-6">
                      Agrega número adicional
                    </h3>
                    <p class="text-sm mt-3 text-gray-900">
                      Añade números adicionales y
                      <br />
                      mejora tu plan existente con más
                      <br />
                      opciones de contacto.
                    </p>
                    <div
                      class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch"
                    >
                      <div class="mt-6 flex items-center gap-x-4">
                        <p class="text-gray-900 text-5xl font-bold tracking-tight">$10</p>
                        <div class="text-sm font-bold text-gray-900">
                          <p>USD</p>
                          <p>Mensual</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!--            cadrd 333    -->

                <div
                  class="bg-gray-800/80 ring-1 ring-white/10 lg:bg-transparent lg:pb-14 lg:ring-0 relative rounded-2xl"
                >
                  <div class="p-8 lg:pt-12 xl:p-6 xl:pt-8">
                    <h3 class="text-white text-lg font-semibold leading-6">
                      Capacitación para empleados
                    </h3>
                    <p class="text-sm mt-2 text-gray-300">
                      Convierte a tus empleados en
                      <br />
                      expertos de nuestra plataforma.
                    </p>
                    <div
                      class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-stretch"
                    >
                      <div class="mt-6 flex items-center gap-x-4">
                        <p class="text-white text-4xl font-bold tracking-tight">$100</p>
                        <div class="text-xs font-bold text-white">
                          <p>USD</p>
                          <p>2 horas</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!--          HASTA AQUI      -->
              </div>
            </div>
          </div>

          <div id="questions" class="relative bg-gray-50 lg:pt-8">
            <div class="mx-auto max-w-7xl px-6 pt-24 sm:pt-32 lg:px-8 pb-8 border-b">
              <h2
                class="text-gray-800 text-center md:text-start text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-medium"
              >
                Preguntas frecuentes
              </h2>
            </div>

            <div class="mx-auto max-w-7xl px-6 lg:px-8">
              <div>
                <div
                  v-for="(faq, index) in faqs"
                  :key="index"
                  @click="toggleAnswer(index)"
                  class="border-b py-6 cursor-pointer"
                >
                  <div class="flex justify-between">
                    <p
                      class="font-bold md:font-semibold text-base text-black/80 xl:text-lg cursor-pointer"
                    >
                      {{ faq.question }}
                    </p>
                    <div class="flex items-center mr-4">
                      <Image
                        v-if="!faq.showAnswer"
                        src="/images/icons/right-chevron.png"
                        alt="Alert Icon"
                        class="ml-2 !w-3 !h-3 cursor-pointer"
                      />
                      <Image
                        v-else
                        src="/images/icons/down-chevron.png"
                        alt="Alert Icon"
                        class="ml-2 !w-3 !h-3 cursor-pointer"
                      />
                    </div>
                  </div>
                  <p
                    v-if="faq.showAnswer"
                    class="mt-5 text-gray-600"
                    v-motion
                    :initial="{ opacity: 0, y: 15 }"
                    :visible-once="{
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        delay: 200,
                        duration: 800,
                        opacity: {
                          duration: 400
                        }
                      }
                    }"
                  >
                    {{ faq.answer }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <footer-navigation />
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Image } from '@unpic/vue';
import FooterNavigation from '@/components/FooterNavigation.vue';

const faqs = ref([
  {
    question: '¿Tiene costo la implementación inicial de Orqui AI?',
    answer:
      'No, la implementación inicial de Orqui AI está incluida en el plan. No hay cargos adicionales por la configuración inicial.',
    showAnswer: false
  },
  {
    question: '¿Cuántos usuarios pueden utilizar Orqui AI?',
    answer:
      'Cada plan de Orqui AI tiene un número específico de usuarios permitidos incluido en el costo base. Sin embargo, no hay límites en la cantidad de usuarios que puedes agregar. Si deseas añadir más usuarios más allá de la cantidad permitida en tu plan, habrá un pequeño costo adicional.',
    showAnswer: false
  },
  {
    question: '¿Qué sucede cuando me acabo el bono de Orqui?',
    answer:
      'Es importante tener en cuenta que, una vez agotado el bono, los mensajes salientes tienen un valor de $0.06 por conversación. Este importe es cobrado por la plataforma meta y nosotros no añadimos valores adicionales.',
    showAnswer: false
  },
  {
    question: '¿Qué pasa cuando se agota el crédito de GPT?',
    answer:
      'Cuando el crédito de GPT se agota, el procesamiento de mensajes podría verse afectado. Nuestros planes están diseñados para abarcar todo el mes, pero en caso de agotamiento, puedes comunicarte con nuestro equipo de soporte. Ofrecemos la opción de recargar más crédito con el importe cargado en tu próxima factura. De lo contrario, tus asesores humanos pueden seguir atendiendo a los clientes para evitar interrupciones en el servicio.',
    showAnswer: false
  },
  {
    question: '¿En qué se diferencia Orqui de los chatbots tradicionales?',
    answer:
      'Orqui AI utiliza tecnología avanzada de procesamiento del lenguaje natural para comprender mejor las consultas de los usuarios y ofrecer respuestas más precisas y personalizadas en comparación con los chatbots tradicionales que sus respuestas ya están previamente programadas.',
    showAnswer: false
  },
  {
    question: '¿Cómo puedo contactar al soporte técnico si tengo más preguntas?',
    answer:
      'Puedes ponerte en contacto con nuestro equipo de soporte técnico a través de nuestro formulario de contacto en la página web o enviando un correo electrónico a gerencia.softdevel@gmail.com. Estamos aquí para ayudarte con cualquier pregunta o inquietud que puedas tener.',
    showAnswer: false
  }
]);

const toggleAnswer = (index) => {
  faqs.value[index].showAnswer = !faqs.value[index].showAnswer;
};

const frequencies = [
  { value: 'MENSUAL', label: 'Mensual' },
  { value: 'ANUAL', label: 'Anual' }
];
const tiers = [
  {
    name: 'Básico',
    id: 'tier-starter',
    href: '#',
    featured: false,
    description: 'All your essential business finances, taken care of.',
    price: { MENSUAL: '49', ANUAL: '44' },
    discount: { MENSUAL: '', ANUAL: '10' },
    primaryList: ['DESCRIPCIÓN GENERAL', '1 Número Integrado', '3 Asesores', '$5 Bono de Orqui'],
    secondaryList: [
      'ASPECTOS DESTACADOS',
      'Bandeja de entrada universal',
      'Chats de equipo',
      'Soporte Inicial ',
      'Agentes IA'
    ]
  },
  {
    name: 'Profesional',
    id: 'tier-scale',
    href: '#',
    featured: true,
    description: 'The best financial services for your thriving business.',
    price: { MENSUAL: '99', ANUAL: '89' },
    discount: { MENSUAL: '', ANUAL: '10' },
    primaryList: [
      'DESCRIPCIÓN GENERAL',
      '3 Números Integrados',
      '10 Asesores',
      '$15 Bono de Orqui'
    ],
    secondaryList: [
      'TODO DE BÁSICO MAS ',
      'Boletines de Whatsapp',
      'Entrenamiento de usuario',
      'Soporte Whatsapp ',
      'Analítica'
    ]
  },
  {
    name: 'Premium',
    id: 'tier-growth',
    href: '#',
    featured: false,
    description: 'Convenient features to take your business to the next level.',
    price: { MENSUAL: '279', ANUAL: '240' },
    discount: { MENSUAL: '', ANUAL: '14' },
    primaryList: [
      'DESCRIPCIÓN GENERAL',
      '5 Números Integrados',
      '25 Asesores',
      '$50 Bono de Orqui'
    ],
    secondaryList: [
      'TODO DE PROFESIONAL MAS ',
      'Formación para empleados',
      'Multiples integraciones',
      'Soporte premium',
      'Aplicación móvil'
    ]
  }
];
const sections = [
  {
    name: 'Descripción general',
    features: [
      {
        name: 'Canales de comunicación',
        tooltip: '',
        tiers: {
          Básico: 'WhatsApp, Instagram, Messenger',
          Profesional: 'WhatsApp, Instagram, Messenger',
          Premium: 'WhatsApp, Instagram, Messenger'
        }
      },
      {
        name: 'Integraciones',
        tooltip:
          'Podemos integrar tu CRM, comercio electrónico o cualquier tienda online de tus redes sociales de forma automática ',
        tiers: { Básico: '1', Profesional: '3', Premium: '5' }
      },
      {
        name: 'Números integrados',
        tooltip:
          'Números de teléfono móvil que puedes utilizar en tu plan.Orqui te regala el primero!!!',
        tiers: { Básico: '1', Profesional: '3', Premium: '5' }
      },
      {
        name: 'Asesores',
        tooltip: 'Cantidad de usuarios que puedes conectar a la suite de mensajerías ',
        tiers: { Básico: '3', Profesional: '10', Premium: '25' }
      },
      {
        name: 'GPT crédito',
        tooltip:
          'Saldo disponible para las conversaciones de los agentes de IA. Los números de conversaciones son referenciales',
        tiers: {
          Básico: '$5 (1500 conversaciones)',
          Profesional: '$15 (4500 conversaciones)',
          Premium: '$50 (12000 conversaciones)'
        }
      }
    ]
  },
  {
    name: 'Conversaciones de WhatsApp',
    features: [
      {
        name: 'Iniciado por contacto',
        tooltip:
          'Las primeras 1000 conversaciones entrantes son gratuitas, $0,06 por cada adicional por meta.Orqui no cobra ningún valor adicional',
        tiers: {
          Básico: '1000 mensuales',
          Profesional: '1000 mensuales',
          Premium: '1000 mensuales'
        }
      },
      {
        name: 'Iniciado por la empresa',
        tooltip:
          'Las conversaciones iniciadas por el usuario son gratuitas hasta consumir el bono especificado,$0,06 por cada adicional por meta.Los valores de conversaciones son referenciales',
        tiers: { Básico: '$0.06', Profesional: '$0.06', Premium: '$0.06' }
      },
      {
        name: 'Bono de Orqui AI',
        tooltip:
          'Orqui te regala un bono mensual que puedes utilizar para conversaciones de whatsapp',
        tiers: {
          Básico: '$5 (125 conversaciones)',
          Profesional: '$15 (375 conversaciones)',
          Premium: '$50 (1250 conversaciones)'
        }
      }
    ]
  },
  {
    name: 'Funciones',
    features: [
      {
        name: 'Bandeja de entrada universal',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Agentes IA',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Etiquetas de chats',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Asignación a asesores',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Aplicación móvil',
        tooltip: '',
        tiers: { Básico: false, Profesional: false, Premium: true }
      },
      {
        name: 'Lectura de imágenes',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Procesamiento de  audio',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Analítica',
        tooltip: 'Módulo donde puedes analizar todas las métricas con respecto a tus mensajes',
        tiers: { Básico: false, Profesional: true, Premium: true }
      }
    ]
  },
  {
    name: 'Boletín WhatsApp',
    features: [
      {
        name: 'Número de contactos',
        tooltip: 'Cantidad de contactos que le puedes enviar boletines a la vez \n',
        tiers: { Básico: false, Profesional: '25', Premium: '25' }
      },
      {
        name: 'Precio',
        tooltip:
          'Precio cobrado por meta en mensajes publicitarios o de marketing salientes, Orqui no cobra valores adicionales.',
        tiers: { Básico: false, Profesional: '$0.07', Premium: '$0.07' }
      },
      {
        name: 'Métricas de rendimiento',
        tooltip: '',
        tiers: { Básico: false, Profesional: true, Premium: true }
      }
    ]
  },
  {
    name: 'Chat de equipo',
    features: [
      {
        name: 'Mensajes directos',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Mensajes grupales',
        tooltip: '',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Mensajes de agentes IA',
        tooltip:
          'La IA tiene la capacidad de escribir a miembros del equipo para aclarar consultas de clientes en la que no domine la respuesta \n',
        tiers: { Básico: true, Profesional: true, Premium: true }
      }
    ]
  },
  {
    name: 'Soporte',
    features: [
      {
        name: 'Canales',
        tooltip: '',
        tiers: {
          Básico: 'Correo',
          Profesional: 'Correo, WhatsApp',
          Premium: 'Correo, Whatsapp, Teléfono'
        }
      },
      {
        name: 'Prioridad',
        tooltip: '',
        tiers: { Básico: 'Normal', Profesional: 'Normal', Premium: 'Alta' }
      },
      {
        name: 'Inicial',
        tooltip: 'Nuestros expertos guiaran en la implementación inicial de Orqui junto a usted',
        tiers: { Básico: true, Profesional: true, Premium: true }
      },
      {
        name: 'Entrenamiento de usuario',
        tooltip: '',
        tiers: { Básico: false, Profesional: true, Premium: true }
      },
      {
        name: 'Formación para empleados ',
        tooltip: '',
        tiers: { Básico: false, Profesional: false, Premium: true }
      }
    ]
  }
];

const frequency = ref(frequencies[0]);

const selectFrequency = (option) => {
  frequency.value = option;
};
</script>

<style>
.padding-header {
  padding-top: 56px;

  @media (min-width: 640px) {
    padding-top: 40px; /* sm */
  }

  @media (min-width: 768px) {
    padding-top: 40px; /* md */
  }

  @media (min-width: 1024px) {
    padding-top: 72px; /* lg */
  }

  @media (min-width: 1280px) {
    padding-top: 73px; /* xl */
  }
}
</style>
